import L from 'leaflet'

const IGN = 'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&STYLE=normal&TILEMATRIXSET=PM'
const ESRI_IMAGERY = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'

// The Géoplateforme only serves France: elsewhere a worldwide layer shows through.
const FRANCE_BOUNDS = [[41.2, -5.5], [51.2, 9.9]]

const FRANCE_CENTER = [46.6, 2.5]
const FRANCE_ZOOM   = 6

export const createBaseLayers = () => {
  const osm = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom:     19,
    attribution: '© OpenStreetMap'
  })
  const ignPlan = L.tileLayer(`${IGN}&FORMAT=image/png&LAYER=GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}`, {
    minZoom: 5, maxZoom: 18, tileSize: 256, bounds: FRANCE_BOUNDS, attribution: 'IGN-F / Géoplateforme'
  })
  const ignOrtho = L.tileLayer(`${IGN}&FORMAT=image/jpeg&LAYER=ORTHOIMAGERY.ORTHOPHOTOS&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}`, {
    minZoom: 5, maxZoom: 19, tileSize: 256, bounds: FRANCE_BOUNDS, attribution: 'IGN-F / Géoplateforme'
  })
  const worldImagery = L.tileLayer(ESRI_IMAGERY, {
    maxZoom:     19,
    attribution: 'Esri, Maxar, Earthstar Geographics'
  })
  const plan      = L.layerGroup([L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OpenStreetMap' }), ignPlan])
  const satellite = L.layerGroup([worldImagery, ignOrtho])
  const adminLimits = L.tileLayer.wms('https://data.geopf.fr/wms-r/wms', {
    layers:  'LIMITES_ADMINISTRATIVES_EXPRESS.LATEST',
    format:  'image/png',
    version: '1.3.0',
    transparent: true,
    opacity:     0.7,
    attribution: 'IGN-F'
  })

  return { osm, plan, satellite, adminLimits }
}

export const createBaseMap = (element, options = {}) => {
  const map = L.map(element, {
    zoomControl:  true,
    preferCanvas: false,
    ...options.map
  }).setView(options.center || FRANCE_CENTER, options.zoom || FRANCE_ZOOM)

  addBaseLayers(map, options)

  if (typeof ResizeObserver !== 'undefined') {
    const observer = new ResizeObserver(() => {
      map.invalidateSize()
      if (options.onResize) options.onResize(map)
    })
    observer.observe(element)
    map.__resizeObserver = observer
  }
  setTimeout(() => {
    map.invalidateSize()
    if (options.onResize) options.onResize(map)
  }, 300)

  return map
}

export const addBaseLayers = (map, options = {}) => {
  const { osm, plan, satellite, adminLimits } = createBaseLayers()

  const baseLayers = { OpenStreetMap: osm, Plan: plan, Satellite: satellite }

  ;(baseLayers[options.baseLayer] || osm).addTo(map)

  L.control.layers(
    baseLayers,
    { [options.overlayLabel || 'Limites administratives']: adminLimits },
    { collapsed: true, position: options.layersPosition || 'topright' }
  ).addTo(map)
}

// fitBounds on a zero-sized container frames the map on nothing, so callers wait
// for a real size first.
export const hasUsableSize = map => {
  if (!map) return false
  const size = map.getSize()

  return size.x > 50 && size.y > 50
}

export const destroyBaseMap = map => {
  if (!map) return
  if (map.__resizeObserver) map.__resizeObserver.disconnect()
  map.remove()
}

const MARKER_HEIGHT = 34

// L.icon needs both dimensions; squaring them squashed every pin.
export const markerIcon = url => {
  if (!url) return pinIcon('#2563eb')

  return L.divIcon({
    className:   '',
    iconSize:    [MARKER_HEIGHT, MARKER_HEIGHT],
    iconAnchor:  [MARKER_HEIGHT / 2, MARKER_HEIGHT],
    popupAnchor: [0, -MARKER_HEIGHT + 2],
    html: `<div class="map-pin" style="display:flex;align-items:flex-end;justify-content:center;height:${MARKER_HEIGHT}px;">
      <img src="${url}" style="height:${MARKER_HEIGHT}px;width:auto;max-width:none;display:block;">
    </div>`
  })
}

export const dotIcon = color => L.divIcon({
  className:   '',
  iconSize:    [14, 14],
  iconAnchor:  [7, 7],
  popupAnchor: [0, -8],
  html: `<div class="map-pin" style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.35);"></div>`
})

export const pinIcon = (color = '#2563eb') => L.divIcon({
  className:   '',
  iconSize:    [28, 36],
  iconAnchor:  [14, 34],
  popupAnchor: [0, -32],
  html: `<div class="map-pin"><svg width="28" height="36" viewBox="0 0 28 36" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 0C6.3 0 0 6.3 0 14c0 9.8 14 22 14 22s14-12.2 14-22C28 6.3 21.7 0 14 0z" fill="${color}" stroke="#fff" stroke-width="2.5"/>
    <circle cx="14" cy="14" r="5" fill="#fff"/></svg></div>`
})

const STATUS_COLORS = { open: '#ef5350', scheduled: '#f4c20d', in_progress: '#3b8de0', closed: '#4cc35e' }

export const stopPinHtml = stop => {
  const color = STATUS_COLORS[stop.state] || (stop.status === 'done' ? STATUS_COLORS.closed : STATUS_COLORS.scheduled)
  return `<div class="map-pin tour-stop tour-stop--${stop.status}" style="--tour-color:${color}">
      <svg width="30" height="38" viewBox="0 0 30 38" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 23 15 23s15-12.5 15-23C30 6.7 23.3 0 15 0z" fill="${color}" stroke="#fff" stroke-width="2.5"/>
        <text x="15" y="20" text-anchor="middle" font-size="13" font-weight="700" fill="#fff" font-family="-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif">${stop.index}</text>
      </svg>
    </div>`
}

export const stopIcon = stop => L.divIcon({
  className:   '',
  iconSize:    [30, 38],
  iconAnchor:  [15, 36],
  popupAnchor: [0, -34],
  html:        stopPinHtml(stop)
})

export const vanHtml = color => `<div class="tour-van" style="--tour-color:${color}"><i class="fas fa-truck"></i></div>`

export const vanIcon = color => L.divIcon({
  className:   '',
  iconSize:    [34, 34],
  iconAnchor:  [17, 17],
  html:        vanHtml(color)
})
